import { DashboardStatus, TDashboardGroup } from 'types/GlobalDashboardGroup'
import { TReport } from 'types/GlobalReport'
import type { TDashboardFolderDto } from '@homepal/api-contract'
import { TResource } from 'types/GlobalResource'

import { useMediaQuery, useTheme } from '@mui/material'
import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {
  useHistory,
  useLocation,
  useParams as useParamsRouter
} from 'react-router-dom'
import { useAccounts } from 'redux/hooks/Accounts'
import { useReports } from 'redux/hooks/Reports'

import { formatUsername, getThumbnail, isApplePlatform } from './functions'
import {
  CLOCK_REFRESH_CHECK_INTERVAL_MS,
  MIN_AUTO_REFRESH_SECONDS
} from './constants'
import { parseQueryParamToNumber } from './queryHandler'
import { useAvailableDashboardGroupsIncludeDashboards } from 'redux/hooks/DashboardGroups'
import { EPublicationStatus } from 'types/Enums'
import { sortDateDesc } from './sortHelper'

export function useParams<T extends Record<string, string>>() {
  const params = useParamsRouter<T>()

  const cleanedParams = Object.keys(params).reduce((acc, key) => {
    const typedKey = key as keyof T
    const value = params[typedKey]
    const cleanValue = value.includes('?') ? value.split('?')[0] : value
    // Cast the cleanValue to T[keyof T] to satisfy TypeScript

    acc[typedKey] = cleanValue as T[keyof T]

    return acc
  }, {} as Partial<T>) as T

  return cleanedParams
}

type ClockTime = { hours: number; minutes: number }

// `refresh_at` is a comma separated list of 24-hour HH:MM times. Malformed
// entries are dropped rather than corrected.
function parseClockTimes(value: string | null): ClockTime[] {
  if (!value) return []

  return value.split(',').reduce<ClockTime[]>((acc, entry) => {
    const match = entry.trim().match(/^(\d{1,2}):(\d{2})$/)

    if (!match) return acc

    const hours = Number(match[1])
    const minutes = Number(match[2])

    if (hours > 23 || minutes > 59) return acc

    acc.push({ hours, minutes })

    return acc
  }, [])
}

// Earliest of the given times that lies strictly in the future. Built from
// local time so it survives DST shifts.
function nextClockOccurrence(times: ClockTime[], from: Date): number | null {
  if (times.length === 0) return null

  const candidates = times.map(({ hours, minutes }) => {
    const next = new Date(from)

    next.setHours(hours, minutes, 0, 0)

    if (next.getTime() <= from.getTime()) {
      next.setDate(next.getDate() + 1)
    }

    return next.getTime()
  })

  return Math.min.apply(null, candidates)
}

/**
 * Reloads the page either on the interval given by the `refresh` query param,
 * in seconds, or at the wall-clock times given by `refresh_at`. Intervals
 * below MIN_AUTO_REFRESH_SECONDS are ignored rather than raised to the floor,
 * so a mistyped url stays inert instead of hammering.
 */
export function useAutoRefresh() {
  const location = useLocation()

  const seconds = useMemo(() => {
    const value = parseQueryParamToNumber(
      new URLSearchParams(location.search).get('refresh')
    )

    if (value === undefined || value < MIN_AUTO_REFRESH_SECONDS) return null

    return value
  }, [location.search])

  const clockTimes = useMemo(
    () =>
      parseClockTimes(new URLSearchParams(location.search).get('refresh_at')),
    [location.search]
  )

  useEffect(() => {
    if (seconds === null) return

    const interval = setInterval(() => window.location.reload(), seconds * 1000)

    return () => clearInterval(interval)
  }, [seconds])

  useEffect(() => {
    // Polling the wall clock rather than sleeping until the target keeps this
    // correct across DST, a suspended screen and a drifting device clock.
    const target = nextClockOccurrence(clockTimes, new Date())

    if (target === null) return

    const interval = setInterval(() => {
      if (Date.now() >= target) window.location.reload()
    }, CLOCK_REFRESH_CHECK_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [clockTimes])
}

export const useIsMobile = (): boolean => {
  const theme = useTheme()

  return useMediaQuery(theme.breakpoints.down('mobile'), {
    noSsr: true
  })
}

type Query = Record<string, string | null>

export function useQuery() {
  const location = useLocation()
  const history = useHistory()

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  // read
  const query = useMemo<Query>(() => {
    const entries: Query = {}

    Array.from(searchParams.entries()).forEach(([key, value]) => {
      entries[key] = value
    })

    return entries
  }, [searchParams])

  // set
  const setQuery = useCallback(
    (newFilters: Query) => {
      const updatedParams = new URLSearchParams(location.search)

      Object.entries(newFilters).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          updatedParams.delete(key)
        } else {
          updatedParams.set(key, value)
        }
      })

      history.push({ search: updatedParams.toString() })
    },
    [location.search, history]
  )

  //clear
  const clearQuery = useCallback(() => {
    history.push({ search: '' })
  }, [history])

  return { clearQuery, query, setQuery }
}

export function usePrevious<T>(value: T) {
  const ref = useRef<T | undefined>(undefined)

  useEffect(() => {
    ref.current = value
  }, [value])

  return ref.current
}

export function useShortcutLabel(key: string | undefined) {
  const [isApple, setIsApple] = useState(false)

  useEffect(() => {
    setIsApple(isApplePlatform())
  }, [])

  if (!key) return null

  const label = isApple ? `⌘ ${key}` : `Ctrl + ${key}`
  const aria = isApple ? `Meta+${key}` : `Control+${key}`

  return { isApple, label, aria }
}

export function useResources(
  params: {
    dashboardGroups?: TDashboardGroup[]
    folders?: TDashboardFolderDto[]
    reports?: TReport[]
  },
  sortOn?: 'visitedAt'
): TResource[] {
  const { accounts } = useAccounts()
  const folders =
    params.folders?.map((folder) => {
      return {
        href: `/resources/folders/${folder.id}`,
        id: folder.id,
        title: folder.name,
        updatedAt: folder.updated_at.toString(),
        variant: 'folder' as const,
        visitedAt: folder.updated_at.toString() // $TODO: HP-14585
      }
    }) ?? []

  const dashboardGroups =
    params.dashboardGroups?.map((dashboardGroup) => {
      const owner = accounts.find((a) => a.id === dashboardGroup.owner) ?? null

      const src = getThumbnail(dashboardGroup.thumbnail ?? null)

      return {
        favorite: dashboardGroup.favorite,
        folderId: dashboardGroup.folder_id,
        href: `/dashboards/${dashboardGroup.firstDashboardId}`,
        id: dashboardGroup.id,
        owner: owner
          ? {
              email: owner.email,
              name: formatUsername(owner)
            }
          : null,
        src: src,
        status:
          dashboardGroup.status === DashboardStatus.DRAFT
            ? EPublicationStatus.DRAFT
            : EPublicationStatus.PUBLISHED, // $TODO
        title: dashboardGroup.title,
        updatedAt: dashboardGroup.updated_at.toString(),
        variant: 'dashboard' as const,
        visitedAt: dashboardGroup.latest_visit
          ? new Date(dashboardGroup.latest_visit).toISOString()
          : undefined
      }
    }) || []

  const reports =
    params.reports?.map((report) => {
      const owner = accounts.find((a) => a.id === report.owner) ?? null

      const src = getThumbnail(report.thumbnail ?? null)

      return {
        favorite: report.favorite,
        folderId: report.folder_id,
        href: `/reports/${report.id}`,
        id: report.id,
        owner: owner
          ? {
              email: owner.email,
              name: formatUsername(owner)
            }
          : null,
        src: src,
        status: report.status,
        title: report.title,
        updatedAt: report.updated_at.toString(),
        variant: 'report' as const,
        visitedAt: report.visited_at
          ? new Date(report.visited_at).toISOString()
          : undefined
      }
    }) || []

  const resources = [...folders, ...dashboardGroups, ...reports]

  if (sortOn === 'visitedAt') {
    return [...resources].sort((a, b) =>
      sortDateDesc(a.visitedAt ?? null, b.visitedAt ?? null)
    )
  }

  return resources
}

// Placeholder:
export function useAllResources(): TResource[] {
  const dashboardGroups = useAvailableDashboardGroupsIncludeDashboards()
  const reports = useReports()

  return useResources({ dashboardGroups, reports })
}

export function useOnClickOutside<T extends HTMLElement>(
  ref: RefObject<T | null>,
  handler: (event: Event) => void,
  when = true
) {
  useEffect(() => {
    if (!when) return

    const listener = (event: Event) => {
      const el = ref.current

      if (!el) return

      // If clicking inside, ignore
      if (el.contains(event.target as Node)) return

      handler(event)
    }

    // pointerdown catches mouse + touch and fires before focus/blur
    document.addEventListener('pointerdown', listener, true)

    return () => {
      document.removeEventListener('pointerdown', listener, true)
    }
  }, [ref, handler, when])
}

const DEFAULT_DOCUMENT_TITLE = 'Homepal'

/**
 * Sets the browser tab title so that every page is
 * identified by its own content, and restores the
 * default title when the component unmounts.
 */
export function useDocumentTitle(title: string | null | undefined) {
  useEffect(() => {
    document.title = title
      ? `${title} | ${DEFAULT_DOCUMENT_TITLE}`
      : DEFAULT_DOCUMENT_TITLE

    return () => {
      document.title = DEFAULT_DOCUMENT_TITLE
    }
  }, [title])
}
